import { useMutation, MutationOptions } from "@tanstack/react-query";
import {
	CreateOnlineCourseV2DTO,
	restRepository,
	SaveDraftOnlineCourseDTO,
	UpdateOnlineCourseDisplayDto,
	UpdateOnlineCourseV2DTO,
	UserLessonProgressDTO,
} from "@onair/repositories";
import { onlineCourseService } from "@onair/services";
import {
	bufferLessonProgress,
	BufferLessonProgressInput,
	markLessonCompleted,
} from "@onair/services/online-course";
import {
	DELETE_ONLINE_COURSE_V2_BY_ADMIN,
	ENABLE_LND_COURSE,
} from "../../keys";

const callWithAdminOption = async <T>(
	isAdmin: boolean,
	adminFn: () => Promise<{ data: T }>,
	userFn: () => Promise<{ data: T }>,
): Promise<T> => {
	const response = isAdmin ? await adminFn() : await userFn();
	return response.data;
};

export const useUpdateOnlineCourseDisplayedByIdMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async (
			data: { isAdmin: boolean; id: string } & UpdateOnlineCourseDisplayDto,
		) => {
			const { isAdmin, id, ...restData } = data;

			return await callWithAdminOption(
				isAdmin,
				() =>
					restRepository.api.onlineCourseUpdateDisplayedByIdByAdmin(
						id,
						restData,
					),
				() => restRepository.api.onlineCourseUpdateDisplayedById(id, restData),
			);
		},
	});
};

export const useDeleteOnlineCourseMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async ({ isAdmin, id }: { isAdmin: boolean; id: string }) => {
			return await callWithAdminOption(
				isAdmin,
				() => restRepository.api.onlineCourseDeleteByIdByAdmin(id),
				() => restRepository.api.onlineCourseDeleteById(id),
			);
		},
	});
};

export const useCreateOnlineCourseV2Mutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async (data: CreateOnlineCourseV2DTO) => {
			return await onlineCourseService.createOnlineCourseV2(data);
		},
	});
};

export const useUpdateOnlineCourseV2Mutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async (data: { id: string } & UpdateOnlineCourseV2DTO) => {
			const { id, ...updateData } = data;
			return await onlineCourseService.updateOnlineCourseV2(id, updateData);
		},
	});
};

export const useDeleteOnlineCourseV2ByAdminMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationKey: [DELETE_ONLINE_COURSE_V2_BY_ADMIN],
		mutationFn: async (courseId: string) => {
			return await onlineCourseService.deleteOnlineCourseV2ByAdmin(courseId);
		},
	});
};

export const useEnableLnDCourseMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationKey: [ENABLE_LND_COURSE],
		mutationFn: async (courseId: string) => {
			return await onlineCourseService.enableLnDCourse(courseId);
		},
	});
};

export const useSaveOnlineCourseV2DraftMutation = (options = {}) => {
	return useMutation({
		...options,
		mutationFn: async (data: SaveDraftOnlineCourseDTO) => {
			return await onlineCourseService.saveOnlineCourseV2Draft(data);
		},
	});
};

export const useMutationBufferLessonProgress = (
	options?: MutationOptions<void, Error, BufferLessonProgressInput>,
) => {
	return useMutation({
		mutationFn: (input: BufferLessonProgressInput) =>
			bufferLessonProgress(input),
		...options,
	});
};

export const useMutationMarkLessonCompleted = (
	options?: MutationOptions<UserLessonProgressDTO, Error, string>,
) => {
	return useMutation({
		mutationFn: (lessonId: string) => markLessonCompleted(lessonId),
		...options,
	});
};
